import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { regionDetection } from '@/helpers/regionDetection';
import { ILocation } from '~/helpers/gymClusters';
import { IStandardPackages } from '~/helpers/fetchStandardPackages';

export type IStaffIds = string[];

export interface UserData {
    email: string | boolean;
    first_name: string | boolean;
    last_name: string | boolean;
    location: string;
    phone?: string;
}

export interface LocationData {
    duration: string;
    min_lead_time: string;
    max_advance: string;
    timezone: string;
    timezone_name?: string;
    booking_business: string;
    display_name: string;
    address: string;
    latitude?: string;
    longitude?: string;
    staff_ids: IStaffIds;
    country_code: string;
    location_id: string;
    currency: string;
    is_pricing_panel_enabled?: boolean;
}

export interface ExistingBooking {
    appointment_id?: string;
}

export interface BookingState extends UserData, LocationData, ExistingBooking {
    booking_created_date: string;
    selected_date: string;
    start_time: string;
    end_time: string;
    start_date: string;
    end_date: string;
    special_request?: string;
    booking_id?: string;
    email_opt_in?: boolean;
    sms_opt_in?: boolean;
    sms_consent?: boolean;
    whatsapp_consent?: boolean;
    marketing_consent?: boolean;
    staff_id: string;
    cancellation_reason?: string | null;
    cancellation_other?: boolean;
    team?: string;
    cookie?: string;
    utm_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    package_interest?: string;
    gcl_id?: string;
    gbra_id?: string;
    wbra_id?: string;
    enquiry_country_code?: string;
    slide_index?: number;
    experiment?: string;
    gym_switched?: boolean;
    is_loader?: boolean;
    is_direct?: boolean;
    is_duplicate?: boolean;
    whatsapp_service_consent?: boolean;
    sms_service_consent?: boolean;
    is_staff?: boolean;
    is_online?: boolean;
    consultation_types_allowed?: string;
    maps_url?: string;
    service_id?: string;
    signup_fee_paid?: boolean;
    tags?: string[];
    near_by_location?: ILocation[];
    standard_packages?: IStandardPackages[];
    voucher_code?: string;
    fbp?: string;
    fbc?: string;
    client_user_agent?: string;
    event_source_url?: string;
    client_ip_address?: string;
}

export const useBookingStore = defineStore('booking', () => {
    const defaultState = () => {
        return {
            booking_created_date: new Date().toDateString(),
            selected_date: '',
            start_time: '',
            end_time: '',
            duration: '',
            min_lead_time: '',
            max_advance: '',
            timezone: '',
            timezone_name: '',
            booking_business: '',
            display_name: '',
            address: '',
            staff_ids: [],
            staff_id: '',
            email: '',
            first_name: '',
            last_name: '',
            location: '',
            location_id: '',
            phone: '',
            start_date: '',
            end_date: '',
            special_request: '',
            email_opt_in: false,
            sms_opt_in: true,
            sms_consent: false,
            whatsapp_consent: false,
            marketing_consent: false,
            appointment_id: '',
            country_code: 'GB',
            currency: '',
            cookie: '',
            utm_id: '',
            utm_source: '',
            utm_medium: '',
            utm_campaign: '',
            utm_term: '',
            utm_content: '',
            package_interest: '',
            gcl_id: '',
            gbra_id: '',
            wbra_id: '',
            gym_switched: false,
            is_loader: false,
            is_staff: false,
            is_direct: false,
            is_duplicate: false,
            is_pricing_panel_enabled: false,
            whatsapp_service_consent: false,
            sms_service_consent: false,
            consultation_types_allowed: 'IN-PERSON',
            maps_url: '',
            is_online: false,
            service_id: '',
            signup_fee_paid: false,
            latitude: '',
            longitude: '',
            tags: [],
            near_by_location: [],
            standard_packages: [],
            voucher_code: '',
            fbp: '',
            fbc: '',
            client_user_agent: '',
            event_source_url: '',
            client_ip_address: '',
        };
    };

    const state = ref<BookingState>(defaultState());

    const updateBookingState = (data: Partial<BookingState>) => {
        Object.assign(state.value, data);
    };

    watch(
        state,
        (newValue) => {
            localStorage.setItem('bookingStore', JSON.stringify(newValue));
        },
        { deep: true }
    );

    const getLocalStorage = () => {
        const bookingStore = localStorage.getItem('bookingStore');
        return bookingStore ? JSON.parse(bookingStore) : false;
    };

    const restoreFromLocalStorage = () => {
        const localStorageState = getLocalStorage();
        if (localStorageState) {
            updateBookingState(localStorageState);
            return true;
        }
        return false;
    };

    const regionBasedConsent = () => {
        const presetConsent = regionDetection(state.value.country_code, ['US'])
            ? {
                  sms_service_consent: false,
                  whatsapp_service_consent: false,
              }
            : {
                  sms_service_consent: true,
                  whatsapp_service_consent: true,
              };
        updateBookingState({ ...presetConsent });
    };

    const resetState = () => {
        state.value = { ...defaultState() };
    };

    const isInternal = () => {
        return state.value.team === 'enquiry-team';
    };

    const isExistingBooking = () => !!state.value.booking_id;

    // Staff booking on behalf of a client (walk-in, enquiry team, staff reschedule link)
    const isStaffJourney = () =>
        ['walk-in', 'enquiry-team'].includes(state.value.team ?? '') ||
        !!state.value.is_staff;

    const allowVirtualbookings = (): boolean => {
        return ['BOTH', 'VIRTUAL'].includes(
            state.value.consultation_types_allowed ?? ''
        );
    };

    const setParameters = (params: IEnquiryRouteQueryParams) => {
        const { gclid, gbraid, wbraid, ...rest } = params;
        updateBookingState({
            ...rest,
            ...(gclid && { gcl_id: gclid }),
            ...(gbraid && { gbra_id: gbraid }),
            ...(wbraid && { wbra_id: wbraid }),
        });
    };

    const setExperimentName = (experiment: string) => {
        updateBookingState({ experiment });
    };

    return {
        state,
        updateBookingState,
        restoreFromLocalStorage,
        resetState,
        regionBasedConsent,
        isInternal,
        setParameters,
        setExperimentName,
        allowVirtualbookings,
        isExistingBooking,
        isStaffJourney,
    };
});

export type IEnquiryRouteQueryParams = {
    utm_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    package_interest?: string;
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    cookie?: string;
};
